import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useApolloClient } from "@apollo/client";
import { useAtom } from "jotai";
import { ChevronLeft, Loader2, MessageSquare, Sparkles } from "lucide-react";

import { useLocation } from "@redwoodjs/router";

import { AssistantComposer } from "src/components/AssistantChat/AssistantComposer";
import { AssistantConversation } from "src/components/AssistantChat/AssistantConversation";
import { AssistantHeader } from "src/components/AssistantChat/AssistantHeader";
import { AssistantSessionList } from "src/components/AssistantChat/AssistantSessionList";
import { contactDialogAtom } from "src/components/ContactDialog/ContactDialog";
import { Button } from "src/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "src/components/ui/drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { assistantLaunchRequestAtom } from "src/lib/assistantLaunch";
import { useAuth } from "src/lib/auth";
import { storeIntegrationTypes } from "src/lib/constants";
import { useApiMutation } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { STORE_INSIGHTS_QUERY } from "src/lib/queries/insights";
import {
  LLM_CHAT_ARCHIVE_THREAD_MUTATION,
  LLM_CHAT_SESSIONS_QUERY,
  LLM_CHAT_START_MUTATION,
  LLM_CHAT_STATE_QUERY,
  LLM_CHAT_STATUS_QUERY,
} from "src/lib/queries/llm";
import { cn } from "src/lib/utils";

const THREAD_HISTORY_LIMIT = 150;
const SESSION_LIST_LIMIT = 40;
const PROMPT_SUGGESTION_LIMIT = 6;
const ACTIVE_RUN_POLL_INTERVAL_MS = 1600;
const ACTIVE_RUN_SLOW_POLL_INTERVAL_MS = 3500;
const IDLE_OPEN_POLL_INTERVAL_MS = 20000;
const CLOSED_RUN_POLL_INTERVAL_MS = 2600;
const POLL_SLOW_AFTER_MS = 30000;
const CLOSED_TRIGGER_LABEL_MAX = 28;
const SCROLL_BOTTOM_THRESHOLD_PX = 72;
const ASSISTANT_MESSAGE_TOP_OFFSET_PX = 18;
const DEFAULT_PROGRESS_MESSAGE = "Working...";
const QUEUED_PROGRESS_MESSAGE = "Queued for processing";
const FAILED_RUN_MESSAGE =
  "I couldn't complete that request. Please try again.";
const NEW_CHAT_LOADING_ID = "__new_chat__";

const PROMPT_SUGGESTIONS = [
  {
    label: "Sales vs spend",
    prompt: "Weekly sales vs ad spend (90d)",
    page: "marketing",
  },
  {
    label: "Ad spend by month",
    prompt: "Ad spend by month for the last 6 months",
    page: "marketing",
  },
  {
    label: "Total sales",
    prompt: "Total sales by month for the last 6 months",
  },
  {
    label: "Gross margin",
    prompt: "Gross profit margin for the last 12 months",
  },
  {
    label: "Order count",
    prompt: "Order count for the last 30 days",
  },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const compactLabel = (value, maxLength = CLOSED_TRIGGER_LABEL_MAX) => {
  const text = String(value || "").trim();
  if (!text) return DEFAULT_PROGRESS_MESSAGE;
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

const getStoreDisplayName = (integration) =>
  integration?.description || integration?.domain || "another store";

const getThreadDisplayTitle = (title) => title || "New chat";

const buildTargetFromRun = (run, fallbackStore) => {
  if (!run?.threadId) return null;

  return {
    runId: run.id,
    integrationId: run.integrationId || fallbackStore?.id || null,
    threadId: run.threadId,
    threadTitle: getThreadDisplayTitle(run.threadTitle),
    storeName: run.integrationName || getStoreDisplayName(fallbackStore),
    storeDomain: run.integrationDomain || fallbackStore?.domain || null,
    progressMessage: run.progressMessage || null,
  };
};

const getTargetKey = (target) =>
  target?.integrationId && target?.threadId
    ? `${target.integrationId}:${target.threadId}`
    : null;

const toUiRole = (role) =>
  String(role || "").toUpperCase() === "ASSISTANT" ? "assistant" : "user";

const mapMessagesToUi = (threadMessages = []) =>
  threadMessages.map((message) => ({
    id: message.id,
    role: toUiRole(message.role),
    content: message.content,
    displayKind: message.displayKind || null,
    displayPayload: message.displayPayload || null,
    runId: message.runId,
    status: message.status,
    createdAt: message.createdAt,
  }));

const serializeDisplayPayload = (value) => {
  if (value === null || value === undefined) return "";

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const areMessagesEqual = (currentMessages, nextMessages) => {
  if (currentMessages.length !== nextMessages.length) return false;

  return currentMessages.every((message, index) => {
    const next = nextMessages[index];
    return (
      next &&
      message.id === next.id &&
      message.role === next.role &&
      message.content === next.content &&
      message.displayKind === next.displayKind &&
      serializeDisplayPayload(message.displayPayload) ===
        serializeDisplayPayload(next.displayPayload) &&
      message.status === next.status &&
      message.createdAt === next.createdAt
    );
  });
};

const getScrollAreaViewport = (root) =>
  root?.querySelector('[data-slot="scroll-area-viewport"]');

const getMessageElement = (root, messageId) => {
  if (!root || !messageId) return null;

  return Array.from(root.querySelectorAll("[data-assistant-message-id]")).find(
    (node) => node.dataset.assistantMessageId === messageId
  );
};

const isViewportNearBottom = (viewport) =>
  viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <=
  SCROLL_BOTTOM_THRESHOLD_PX;

const getUpdatedAssistantReply = (currentMessages, nextMessages) => {
  const currentMessagesById = new Map(
    currentMessages
      .filter((message) => message.id)
      .map((message) => [message.id, message])
  );

  return nextMessages
    .filter((message) => {
      if (!message.id || message.role !== "assistant" || !message.content) {
        return false;
      }

      const currentMessage = currentMessagesById.get(message.id);
      if (!currentMessage) return true;

      return (
        currentMessage.role === "assistant" &&
        currentMessage.content !== message.content
      );
    })
    .at(-1);
};

const scrollViewportToBottom = (viewport, behavior = "auto") => {
  if (typeof viewport.scrollTo === "function") {
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior,
    });
    return;
  }

  viewport.scrollTop = viewport.scrollHeight;
};

const scrollViewportToMessage = (
  viewport,
  messageElement,
  behavior = "smooth"
) => {
  if (typeof messageElement.scrollIntoView === "function") {
    const previousScrollMarginTop = messageElement.style.scrollMarginTop;
    messageElement.style.scrollMarginTop = `${ASSISTANT_MESSAGE_TOP_OFFSET_PX}px`;
    messageElement.scrollIntoView({
      block: "start",
      inline: "nearest",
      behavior,
    });
    messageElement.style.scrollMarginTop = previousScrollMarginTop;
    return;
  }

  const viewportRect = viewport.getBoundingClientRect();
  const messageRect = messageElement.getBoundingClientRect();
  const nextTop =
    viewport.scrollTop +
    messageRect.top -
    viewportRect.top -
    ASSISTANT_MESSAGE_TOP_OFFSET_PX;

  if (typeof viewport.scrollTo === "function") {
    viewport.scrollTo({
      top: Math.max(0, nextTop),
      behavior,
    });
    return;
  }

  viewport.scrollTop = Math.max(0, nextTop);
};

const usePageVisibility = () => {
  const [visible, setVisible] = useState(() =>
    typeof document === "undefined"
      ? true
      : document.visibilityState !== "hidden"
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const onVisibilityChange = () => {
      setVisible(document.visibilityState !== "hidden");
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return visible;
};

const StoreAssistantModalContent = () => {
  const [open, setOpen] = useState(false);
  const [mobileSessionsOpen, setMobileSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isComposerMultiline, setIsComposerMultiline] = useState(false);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [loadingThreadId, setLoadingThreadId] = useState(null);
  const [archivingThreadIds, setArchivingThreadIds] = useState(() => new Set());
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [currentRunTarget, setCurrentRunTarget] = useState(null);
  const [runStartedAt, setRunStartedAt] = useState(null);
  const [runProgressMessage, setRunProgressMessage] = useState(
    DEFAULT_PROGRESS_MESSAGE
  );
  const [chatState, setChatState] = useState(null);
  const [promptSuggestionInsights, setPromptSuggestionInsights] = useState([]);
  const [readyTargetsByKey, setReadyTargetsByKey] = useState({});
  const [selectedThreadByStore, setSelectedThreadByStore] = useState({});
  const [revealingMessageIds, setRevealingMessageIds] = useState(
    () => new Set()
  );

  const apolloClient = useApolloClient();
  const { mutationApi: llmChatStart } = useApiMutation({
    mutation: LLM_CHAT_START_MUTATION,
    onError: () => "",
  });
  const { mutationApi: llmChatArchiveThread } = useApiMutation({
    mutation: LLM_CHAT_ARCHIVE_THREAD_MUTATION,
    onError: () => "",
  });
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();
  const isPageVisible = usePageVisibility();
  const { pathname } = useLocation();
  const [, setContactDialogState] = useAtom(contactDialogAtom);
  const [assistantLaunchRequest, setAssistantLaunchRequest] = useAtom(
    assistantLaunchRequestAtom
  );
  const { selectedIntegration: store, setSelectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    { queryOnRender: false }
  );

  const openRef = useRef(open);
  const scrollAreaRef = useRef(null);
  const composerInputRef = useRef(null);
  const messagesRef = useRef([]);
  const scrollIntentRef = useRef(null);
  const animatedAssistantMessageIdsRef = useRef(new Set());
  const pollTokenRef = useRef(0);
  const selectedThreadIdRef = useRef(null);
  const storeIdRef = useRef(null);
  const currentRunTargetRef = useRef(null);
  const selectedThreadByStoreRef = useRef({});
  const pendingOpenTargetRef = useRef(null);
  const pendingAssistantLaunchRef = useRef(null);
  const observedActiveRunRef = useRef(null);
  const mobileSessionsOpenRef = useRef(false);
  const mobileHistoryEntryPushedRef = useRef(false);
  const stateRequestIdRef = useRef(0);
  const sessionsRequestIdRef = useRef(0);

  const canQuery = Boolean(store?.id);
  const creditStatus = chatState?.creditStatus || null;
  const activeRun = chatState?.activeRun || null;
  const activeRunTarget =
    buildTargetFromRun(activeRun, store) || currentRunTarget;
  const activeRunTargetKey = getTargetKey(activeRunTarget);
  const currentThreadKey = getTargetKey({
    integrationId: store?.id,
    threadId: selectedThreadId,
  });
  const activeRunId = activeRunTarget?.runId || currentRunId;
  const isCreditLocked = Boolean(creditStatus?.locked);
  const isRunning = Boolean(activeRunId || isStartingRun);
  const isSelectedThreadRunning = Boolean(
    activeRunTargetKey && activeRunTargetKey === currentThreadKey
  );
  const isCurrentConversationWaiting = Boolean(
    isStartingRun || isSelectedThreadRunning
  );
  const hasAnotherActiveRun = Boolean(
    activeRunTarget && activeRunTargetKey !== currentThreadKey
  );
  const currentStoreActiveThreadId =
    activeRunTarget && store?.id && activeRunTarget.integrationId === store.id
      ? activeRunTarget.threadId
      : null;
  const sourcesOfTruth = chatState?.sourcesOfTruth?.length
    ? chatState.sourcesOfTruth
    : [{ id: "store", label: "Store data", type: "STORE" }];

  const suggestions = useMemo(() => {
    const generatedSuggestions = promptSuggestionInsights
      .filter((insight) => insight.prompt)
      .map((insight) => ({
        label: insight.title,
        prompt: insight.prompt,
      }));
    if (generatedSuggestions.length) {
      return generatedSuggestions.slice(0, 4);
    }

    const path =
      typeof window === "undefined" ? "" : window.location.pathname || "";
    const pageKind = path.includes("marketing") ? "marketing" : "general";
    const prioritized = PROMPT_SUGGESTIONS.filter(
      (suggestion) => !suggestion.page || suggestion.page === pageKind
    );

    return prioritized.slice(0, 4);
  }, [promptSuggestionInsights]);

  const resetComposerHeight = useCallback(() => {
    const node = composerInputRef.current;
    if (!node) return;
    if (!node.value.trim()) {
      node.style.height = "";
      setIsComposerMultiline(false);
      return;
    }

    node.style.height = "auto";
    const nextHeight = Math.min(node.scrollHeight, 176);
    node.style.height = `${nextHeight}px`;
    setIsComposerMultiline(nextHeight > 56);
  }, []);

  const focusComposerInput = useCallback(() => {
    const focusInput = () => {
      composerInputRef.current?.focus();
    };

    if (typeof window === "undefined") {
      focusInput();
      return;
    }

    window.requestAnimationFrame(focusInput);
  }, []);

  const applyMessages = useCallback(
    (nextMessages, { scrollMode = "update" } = {}) => {
      const mapped = mapMessagesToUi(nextMessages);
      const currentMessages = messagesRef.current;

      if (scrollMode === "history") {
        scrollIntentRef.current = { type: "bottom", behavior: "auto" };
      }

      if (scrollMode === "bottom") {
        scrollIntentRef.current = { type: "bottom", behavior: "smooth" };
      }

      if (areMessagesEqual(currentMessages, mapped)) {
        setMessages((currentStateMessages) =>
          areMessagesEqual(currentStateMessages, mapped)
            ? currentStateMessages
            : mapped
        );
        return;
      }

      if (scrollMode !== "history") {
        const newlyArrivedAssistantMessage = getUpdatedAssistantReply(
          currentMessages,
          mapped
        );

        if (newlyArrivedAssistantMessage) {
          const viewport = getScrollAreaViewport(scrollAreaRef.current);
          const latestKnownActiveRunTarget =
            currentRunTargetRef.current || observedActiveRunRef.current;
          const latestKnownActiveRunKey = getTargetKey(
            latestKnownActiveRunTarget
          );
          const currentSelectedKey = getTargetKey({
            integrationId: storeIdRef.current,
            threadId: selectedThreadIdRef.current,
          });
          const isReplyForCurrentRun =
            latestKnownActiveRunKey &&
            latestKnownActiveRunKey === currentSelectedKey;
          const wasAwaitingAssistantReply =
            currentMessages.at(-1)?.role === "user" &&
            mapped.at(-1)?.id === newlyArrivedAssistantMessage.id;

          if (
            isReplyForCurrentRun ||
            wasAwaitingAssistantReply ||
            !viewport ||
            isViewportNearBottom(viewport)
          ) {
            scrollIntentRef.current = {
              type: "messageTop",
              messageId: newlyArrivedAssistantMessage.id,
              behavior: "smooth",
            };
          }

          if (
            !animatedAssistantMessageIdsRef.current.has(
              newlyArrivedAssistantMessage.id
            )
          ) {
            animatedAssistantMessageIdsRef.current.add(
              newlyArrivedAssistantMessage.id
            );
            setRevealingMessageIds(
              (currentRevealingMessageIds) =>
                new Set([
                  ...currentRevealingMessageIds,
                  newlyArrivedAssistantMessage.id,
                ])
            );
          }
        }
      }

      setMessages((currentStateMessages) =>
        areMessagesEqual(currentStateMessages, mapped)
          ? currentStateMessages
          : mapped
      );
      messagesRef.current = mapped;
    },
    []
  );

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  useEffect(() => {
    storeIdRef.current = store?.id || null;
  }, [store?.id]);

  useEffect(() => {
    currentRunTargetRef.current = currentRunTarget;
  }, [currentRunTarget]);

  useEffect(() => {
    selectedThreadByStoreRef.current = selectedThreadByStore;
  }, [selectedThreadByStore]);

  useEffect(() => {
    mobileSessionsOpenRef.current = mobileSessionsOpen;
  }, [mobileSessionsOpen]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!open || !currentThreadKey) return;

    setReadyTargetsByKey((currentReadyTargetsByKey) => {
      if (!currentReadyTargetsByKey[currentThreadKey]) {
        return currentReadyTargetsByKey;
      }

      const nextReadyTargetsByKey = { ...currentReadyTargetsByKey };
      delete nextReadyTargetsByKey[currentThreadKey];
      return nextReadyTargetsByKey;
    });
  }, [currentThreadKey, open]);

  const loadSessions = useCallback(async () => {
    if (!canQuery) return [];

    const integrationId = store.id;
    const requestId = sessionsRequestIdRef.current + 1;
    sessionsRequestIdRef.current = requestId;
    setIsLoadingSessions(true);
    try {
      const { data } = await apolloClient.query({
        query: LLM_CHAT_SESSIONS_QUERY,
        variables: {
          integrationId,
          limit: SESSION_LIST_LIMIT,
        },
        fetchPolicy: "network-only",
      });

      if (
        requestId !== sessionsRequestIdRef.current ||
        integrationId !== storeIdRef.current
      ) {
        return [];
      }

      const nextSessions = data?.llmChatSessions || [];
      setSessions(nextSessions);
      return nextSessions;
    } catch {
      return [];
    } finally {
      if (requestId === sessionsRequestIdRef.current) {
        setIsLoadingSessions(false);
      }
    }
  }, [apolloClient, canQuery, store?.id]);

  const loadPromptSuggestions = useCallback(async () => {
    if (!canQuery) {
      setPromptSuggestionInsights([]);
      return [];
    }

    const integrationId = store.id;
    try {
      const { data } = await apolloClient.query({
        query: STORE_INSIGHTS_QUERY,
        variables: {
          integrationId,
          surface: "CHAT_PROMPT",
          limit: PROMPT_SUGGESTION_LIMIT,
        },
        fetchPolicy: "network-only",
      });

      if (integrationId !== storeIdRef.current) {
        return [];
      }

      const nextSuggestions = data?.storeInsights || [];
      setPromptSuggestionInsights(nextSuggestions);
      return nextSuggestions;
    } catch {
      if (integrationId === storeIdRef.current) {
        setPromptSuggestionInsights([]);
      }
      return [];
    }
  }, [apolloClient, canQuery, store?.id]);

  const rememberSelectedThread = useCallback((integrationId, threadId) => {
    if (!integrationId) return;

    setSelectedThreadByStore((currentSelectedThreadByStore) => {
      const nextSelectedThreadByStore = {
        ...currentSelectedThreadByStore,
        [integrationId]: threadId || null,
      };
      selectedThreadByStoreRef.current = nextSelectedThreadByStore;
      return nextSelectedThreadByStore;
    });

    if (integrationId === storeIdRef.current) {
      selectedThreadIdRef.current = threadId || null;
      setSelectedThreadId(threadId || null);
    }
  }, []);

  const markTargetReplyReady = useCallback((target) => {
    const key = getTargetKey(target);
    if (!key) return;

    const selectedKey = getTargetKey({
      integrationId: storeIdRef.current,
      threadId: selectedThreadIdRef.current,
    });

    if (openRef.current && key === selectedKey) {
      return;
    }

    setReadyTargetsByKey((currentReadyTargetsByKey) => {
      if (currentReadyTargetsByKey[key]) {
        return currentReadyTargetsByKey;
      }

      return {
        ...currentReadyTargetsByKey,
        [key]: target,
      };
    });
  }, []);

  const clearReadyTarget = useCallback((targetOrKey) => {
    const key =
      typeof targetOrKey === "string" ? targetOrKey : getTargetKey(targetOrKey);
    if (!key) return;

    setReadyTargetsByKey((currentReadyTargetsByKey) => {
      if (!currentReadyTargetsByKey[key]) {
        return currentReadyTargetsByKey;
      }

      const nextReadyTargetsByKey = { ...currentReadyTargetsByKey };
      delete nextReadyTargetsByKey[key];
      return nextReadyTargetsByKey;
    });
  }, []);

  const closeMobileHistoryPage = useCallback(() => {
    if (mobileHistoryEntryPushedRef.current && typeof window !== "undefined") {
      mobileHistoryEntryPushedRef.current = false;
      window.history.back();
    }

    setMobileSessionsOpen(false);
  }, []);

  const openMobileHistoryPage = useCallback(() => {
    setMobileSessionsOpen(true);

    if (
      !isMobile ||
      !openRef.current ||
      mobileHistoryEntryPushedRef.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    window.history.pushState(
      {
        ...(window.history.state || {}),
        assistantMobileHistory: true,
      },
      "",
      window.location.href
    );
    mobileHistoryEntryPushedRef.current = true;
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return undefined;

    const onPopState = () => {
      if (!mobileHistoryEntryPushedRef.current) return;

      mobileHistoryEntryPushedRef.current = false;
      setMobileSessionsOpen(false);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [isMobile]);

  const loadChatState = useCallback(
    async ({
      threadId = selectedThreadIdRef.current,
      showLoading = false,
      scrollMode = "update",
    } = {}) => {
      if (!canQuery) return null;

      const integrationId = store.id;
      const requestId = stateRequestIdRef.current + 1;
      stateRequestIdRef.current = requestId;
      if (showLoading) {
        setIsLoadingState(true);
        setLoadingThreadId(threadId || NEW_CHAT_LOADING_ID);
      }

      try {
        const { data } = await apolloClient.query({
          query: LLM_CHAT_STATE_QUERY,
          variables: {
            integrationId,
            threadId,
            limit: THREAD_HISTORY_LIMIT,
          },
          fetchPolicy: "network-only",
        });
        if (
          requestId !== stateRequestIdRef.current ||
          integrationId !== storeIdRef.current
        ) {
          return null;
        }

        const nextState = data?.llmChatState || null;
        setChatState(nextState);
        applyMessages(nextState?.messages || [], { scrollMode });

        const nextActiveRun = nextState?.activeRun || null;
        const nextActiveRunTarget = buildTargetFromRun(nextActiveRun, store);
        const knownCurrentRunTarget = currentRunTargetRef.current;
        const previousActiveRunTarget =
          observedActiveRunRef.current || knownCurrentRunTarget;

        if (nextActiveRunTarget) {
          observedActiveRunRef.current = nextActiveRunTarget;
          currentRunTargetRef.current = nextActiveRunTarget;
          setCurrentRunId(nextActiveRunTarget.runId);
          setCurrentRunTarget(nextActiveRunTarget);
        }

        if (!nextActiveRunTarget) {
          observedActiveRunRef.current = null;
        }

        const completedTarget =
          knownCurrentRunTarget &&
          nextActiveRunTarget?.runId !== knownCurrentRunTarget.runId
            ? knownCurrentRunTarget
            : !nextActiveRunTarget && previousActiveRunTarget
              ? previousActiveRunTarget
              : null;

        if (completedTarget) {
          if (!nextActiveRunTarget) {
            setCurrentRunId(null);
            setCurrentRunTarget(null);
            currentRunTargetRef.current = null;
            setRunStartedAt(null);
            setRunProgressMessage(DEFAULT_PROGRESS_MESSAGE);
          }
          markTargetReplyReady(completedTarget);
          if (completedTarget.integrationId === store?.id) {
            await loadSessions();
          }
        }

        if (nextActiveRun?.progressMessage) {
          setRunProgressMessage(nextActiveRun.progressMessage);
        }

        return nextState;
      } catch {
        return null;
      } finally {
        if (requestId === stateRequestIdRef.current) {
          setIsLoadingState(false);
          setLoadingThreadId(null);
        }
      }
    },
    [
      apolloClient,
      applyMessages,
      canQuery,
      loadSessions,
      markTargetReplyReady,
      store,
    ]
  );

  const loadChatStatus = useCallback(async () => {
    const { data } = await apolloClient.query({
      query: LLM_CHAT_STATUS_QUERY,
      variables: { integrationId: store?.id || null },
      fetchPolicy: "network-only",
    });

    const status = data?.llmChatStatus || null;
    const nextActiveRun = status?.activeRun || null;
    const nextActiveRunTarget = buildTargetFromRun(nextActiveRun, store);
    const knownCurrentRunTarget = currentRunTargetRef.current;
    const previousActiveRunTarget =
      observedActiveRunRef.current || knownCurrentRunTarget;

    setChatState((currentChatState) => ({
      ...(currentChatState || {}),
      activeRun: nextActiveRun,
      creditStatus: status?.creditStatus || currentChatState?.creditStatus,
    }));

    if (nextActiveRunTarget) {
      observedActiveRunRef.current = nextActiveRunTarget;
      currentRunTargetRef.current = nextActiveRunTarget;
      setCurrentRunId(nextActiveRunTarget.runId);
      setCurrentRunTarget(nextActiveRunTarget);
    } else {
      observedActiveRunRef.current = null;
    }

    const completedTarget =
      knownCurrentRunTarget &&
      nextActiveRunTarget?.runId !== knownCurrentRunTarget.runId
        ? knownCurrentRunTarget
        : !nextActiveRunTarget && previousActiveRunTarget
          ? previousActiveRunTarget
          : null;

    if (completedTarget) {
      if (!nextActiveRunTarget) {
        setCurrentRunId(null);
        setCurrentRunTarget(null);
        currentRunTargetRef.current = null;
        setRunStartedAt(null);
        setRunProgressMessage(DEFAULT_PROGRESS_MESSAGE);
      }

      const selectedKey = getTargetKey({
        integrationId: storeIdRef.current,
        threadId: selectedThreadIdRef.current,
      });
      const completedKey = getTargetKey(completedTarget);

      if (completedKey === selectedKey) {
        await loadChatState({
          threadId: completedTarget.threadId,
          scrollMode: "update",
        });
      } else {
        markTargetReplyReady(completedTarget);
        if (completedTarget.integrationId === storeIdRef.current) {
          await loadSessions();
        }
      }
    }

    if (nextActiveRun?.progressMessage) {
      setRunProgressMessage(nextActiveRun.progressMessage);
    }

    return status;
  }, [apolloClient, loadChatState, loadSessions, markTargetReplyReady, store]);

  const selectSession = useCallback(
    async (threadId) => {
      if (!threadId || !store?.id) return;
      if (threadId === selectedThreadId) {
        clearReadyTarget({
          integrationId: store.id,
          threadId,
        });
        closeMobileHistoryPage();
        return;
      }

      scrollIntentRef.current = { type: "bottom", behavior: "auto" };
      rememberSelectedThread(store.id, threadId);
      clearReadyTarget({
        integrationId: store.id,
        threadId,
      });
      messagesRef.current = [];
      setMessages([]);
      setRevealingMessageIds(new Set());
      closeMobileHistoryPage();
      await loadChatState({
        threadId,
        showLoading: true,
        scrollMode: "history",
      });
    },
    [
      clearReadyTarget,
      closeMobileHistoryPage,
      loadChatState,
      rememberSelectedThread,
      selectedThreadId,
      store?.id,
    ]
  );

  const startNewChat = useCallback(() => {
    pollTokenRef.current += 1;
    stateRequestIdRef.current += 1;
    rememberSelectedThread(store?.id, null);
    messagesRef.current = [];
    setMessages([]);
    setInput("");
    setIsComposerMultiline(false);
    setIsLoadingState(false);
    setLoadingThreadId(null);
    closeMobileHistoryPage();
    setRevealingMessageIds(new Set());
    scrollIntentRef.current = { type: "bottom", behavior: "auto" };
    focusComposerInput();
    void loadChatState({ threadId: null, scrollMode: "history" });
  }, [
    closeMobileHistoryPage,
    focusComposerInput,
    loadChatState,
    rememberSelectedThread,
    store?.id,
  ]);

  useEffect(() => {
    if (!assistantLaunchRequest) return;

    const prompt = String(assistantLaunchRequest.prompt || "").trim();
    if (!assistantLaunchRequest.integrationId || !prompt) {
      pendingAssistantLaunchRef.current = null;
      setAssistantLaunchRequest(null);
      return;
    }

    const launchRequest = {
      id: assistantLaunchRequest.id,
      integrationId: assistantLaunchRequest.integrationId,
      prompt,
      displayMessage: assistantLaunchRequest.displayMessage || null,
      displayKind: assistantLaunchRequest.displayKind || null,
      displayPayload: assistantLaunchRequest.displayPayload || null,
      threadTitle: assistantLaunchRequest.threadTitle || null,
      autoSend: assistantLaunchRequest.autoSend === true,
    };

    pendingAssistantLaunchRef.current = launchRequest.autoSend
      ? launchRequest
      : null;

    setOpen(true);

    if (assistantLaunchRequest.integrationId !== store?.id) {
      setSelectedIntegration({ id: assistantLaunchRequest.integrationId });
      return;
    }

    if (launchRequest.autoSend) {
      setChatState(null);
    }

    startNewChat();
    setInput(launchRequest.displayMessage || prompt);
    setIsComposerMultiline(false);
    setAssistantLaunchRequest(null);
    focusComposerInput();
  }, [
    assistantLaunchRequest,
    focusComposerInput,
    setAssistantLaunchRequest,
    setSelectedIntegration,
    startNewChat,
    store?.id,
  ]);

  const archiveSession = useCallback(
    async (threadId) => {
      if (
        !threadId ||
        currentStoreActiveThreadId === threadId ||
        archivingThreadIds.has(threadId)
      ) {
        return;
      }

      setArchivingThreadIds((currentArchivingThreadIds) => {
        if (currentArchivingThreadIds.has(threadId)) {
          return currentArchivingThreadIds;
        }

        return new Set([...currentArchivingThreadIds, threadId]);
      });

      try {
        await llmChatArchiveThread({ threadId });

        if (threadId === selectedThreadId) {
          startNewChat();
        }
        clearReadyTarget({
          integrationId: store?.id,
          threadId,
        });
        await loadSessions();
      } catch {
        return;
      } finally {
        setArchivingThreadIds((currentArchivingThreadIds) => {
          if (!currentArchivingThreadIds.has(threadId)) {
            return currentArchivingThreadIds;
          }

          const nextArchivingThreadIds = new Set(currentArchivingThreadIds);
          nextArchivingThreadIds.delete(threadId);
          return nextArchivingThreadIds;
        });
      }
    },
    [
      archivingThreadIds,
      clearReadyTarget,
      currentStoreActiveThreadId,
      llmChatArchiveThread,
      loadSessions,
      selectedThreadId,
      startNewChat,
      store?.id,
    ]
  );

  const openAssistantTarget = useCallback(
    (target) => {
      if (!target?.integrationId || !target?.threadId) {
        setOpen(true);
        return;
      }

      pendingOpenTargetRef.current = target;
      setOpen(true);

      if (target.integrationId !== storeIdRef.current) {
        setSelectedIntegration({ id: target.integrationId });
        return;
      }

      pendingOpenTargetRef.current = null;
      void selectSession(target.threadId);
    },
    [selectSession, setSelectedIntegration]
  );

  const openContactDialog = useCallback(() => {
    setContactDialogState((state) => ({
      ...state,
      open: true,
      prefillValues: {
        name: currentUser?.name || "",
        email: currentUser?.email || "",
        message: "",
      },
      focusField: "message",
    }));
  }, [currentUser?.email, currentUser?.name, setContactDialogState]);

  useEffect(() => {
    openRef.current = open;
    if (open) {
      scrollIntentRef.current = { type: "bottom", behavior: "auto" };
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !selectedThreadId) return;

    scrollIntentRef.current = { type: "bottom", behavior: "auto" };
  }, [open, pathname, selectedThreadId]);

  useEffect(() => {
    pollTokenRef.current += 1;
    stateRequestIdRef.current += 1;
    sessionsRequestIdRef.current += 1;
    storeIdRef.current = store?.id || null;
    setSessions([]);
    const pendingTarget = pendingOpenTargetRef.current;
    const hasPendingTargetForCurrentStore = Boolean(
      pendingTarget?.threadId &&
        pendingTarget?.integrationId &&
        store?.id &&
        pendingTarget.integrationId === store.id
    );
    const nextSelectedThreadId = hasPendingTargetForCurrentStore
      ? pendingTarget.threadId
      : selectedThreadByStoreRef.current[store?.id] || null;

    if (hasPendingTargetForCurrentStore) {
      pendingOpenTargetRef.current = null;
      setSelectedThreadByStore((currentSelectedThreadByStore) => {
        if (currentSelectedThreadByStore[store.id] === pendingTarget.threadId) {
          return currentSelectedThreadByStore;
        }

        const nextSelectedThreadByStore = {
          ...currentSelectedThreadByStore,
          [store.id]: pendingTarget.threadId,
        };
        selectedThreadByStoreRef.current = nextSelectedThreadByStore;
        return nextSelectedThreadByStore;
      });
    }

    selectedThreadIdRef.current = nextSelectedThreadId;
    setSelectedThreadId(nextSelectedThreadId);
    messagesRef.current = [];
    setMessages([]);
    setInput("");
    setChatState(null);
    setPromptSuggestionInsights([]);
    setIsLoadingState(false);
    setLoadingThreadId(null);
    setArchivingThreadIds(new Set());
    setRevealingMessageIds(new Set());
    scrollIntentRef.current = { type: "bottom", behavior: "auto" };
    animatedAssistantMessageIdsRef.current = new Set();
  }, [store?.id]);

  useEffect(() => {
    if (!canQuery) {
      setPromptSuggestionInsights([]);
      return;
    }

    void loadPromptSuggestions();
  }, [canQuery, loadPromptSuggestions, store?.id]);

  useEffect(() => {
    resetComposerHeight();
  }, [input, resetComposerHeight]);

  useEffect(() => {
    if (!open || !canQuery || !isPageVisible) return;

    let cancelled = false;
    const hydrate = async () => {
      void loadSessions();
      const threadId = selectedThreadIdRef.current;
      const nextState = await loadChatState({
        threadId,
        showLoading: messagesRef.current.length === 0,
        scrollMode: messagesRef.current.length === 0 ? "history" : "update",
      });

      if (cancelled) return;

      if (
        !threadId &&
        nextState?.activeRun?.threadId &&
        nextState.activeRun.integrationId === store?.id
      ) {
        rememberSelectedThread(store.id, nextState.activeRun.threadId);
        await loadChatState({
          threadId: nextState.activeRun.threadId,
          showLoading: true,
          scrollMode: "history",
        });
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [
    canQuery,
    isPageVisible,
    loadChatState,
    loadSessions,
    rememberSelectedThread,
    open,
    store?.id,
  ]);

  useEffect(() => {
    if (!open || !canQuery || !isPageVisible || isLoadingState) {
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      const status = await loadChatStatus();
      const target =
        buildTargetFromRun(status?.activeRun, store) ||
        currentRunTargetRef.current;
      const targetKey = getTargetKey(target);
      const selectedKey = getTargetKey({
        integrationId: storeIdRef.current,
        threadId: selectedThreadIdRef.current,
      });

      if (targetKey && targetKey === selectedKey) {
        await loadChatState({ threadId: selectedThreadIdRef.current });
      }
    };

    const elapsed = runStartedAt ? Date.now() - runStartedAt : 0;
    const interval =
      isRunning || activeRun
        ? elapsed > POLL_SLOW_AFTER_MS
          ? ACTIVE_RUN_SLOW_POLL_INTERVAL_MS
          : ACTIVE_RUN_POLL_INTERVAL_MS
        : IDLE_OPEN_POLL_INTERVAL_MS;

    const intervalId = setInterval(poll, interval);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    activeRun,
    canQuery,
    isPageVisible,
    isLoadingState,
    isRunning,
    loadChatState,
    loadChatStatus,
    open,
    runStartedAt,
    store,
  ]);

  useEffect(() => {
    if (open || !currentRunId || !isPageVisible) return undefined;

    let cancelled = false;
    const pollToken = pollTokenRef.current;

    const pollClosedRun = async () => {
      while (!cancelled && pollToken === pollTokenRef.current) {
        try {
          const status = await loadChatStatus();
          if (status?.activeRun?.id !== currentRunId) {
            return;
          }
        } catch {
          setCurrentRunId(null);
          setCurrentRunTarget(null);
          currentRunTargetRef.current = null;
          setRunStartedAt(null);
          setRunProgressMessage(DEFAULT_PROGRESS_MESSAGE);
          setChatState((currentChatState) =>
            currentChatState?.activeRun?.id === currentRunId
              ? { ...currentChatState, activeRun: null }
              : currentChatState
          );
          return;
        }

        await wait(CLOSED_RUN_POLL_INTERVAL_MS);
      }
    };

    pollClosedRun();
    return () => {
      cancelled = true;
    };
  }, [currentRunId, isPageVisible, loadChatStatus, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const viewport = getScrollAreaViewport(scrollAreaRef.current);
    if (!viewport) return;

    let scrollIntent = scrollIntentRef.current;
    const isWaitingForAssistantAfterUserMessage =
      isCurrentConversationWaiting && messages.at(-1)?.role === "user";

    if (!scrollIntent && isWaitingForAssistantAfterUserMessage) {
      scrollIntentRef.current = { type: "bottom", behavior: "smooth" };
      scrollIntent = scrollIntentRef.current;
    }

    if (!scrollIntent) return;

    if (
      scrollIntent.type === "bottom" &&
      isLoadingState &&
      messages.length === 0
    ) {
      return;
    }

    const runScrollIntent = ({ clearIntent = false } = {}) => {
      const activeScrollIntent = scrollIntentRef.current;
      if (!activeScrollIntent) return;

      if (activeScrollIntent.type === "messageTop") {
        const messageElement = getMessageElement(
          scrollAreaRef.current,
          activeScrollIntent.messageId
        );

        if (messageElement) {
          scrollViewportToMessage(
            viewport,
            messageElement,
            activeScrollIntent.behavior
          );
        } else {
          scrollViewportToBottom(viewport, activeScrollIntent.behavior);
        }
      } else {
        scrollViewportToBottom(viewport, activeScrollIntent.behavior);
      }

      if (clearIntent && !isLoadingState) {
        scrollIntentRef.current = null;
      }
    };

    let secondAnimationFrameId = null;
    const animationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(runScrollIntent);
    });
    const retryTimeoutId = window.setTimeout(
      () => runScrollIntent({ clearIntent: true }),
      180
    );

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      if (secondAnimationFrameId) {
        window.cancelAnimationFrame(secondAnimationFrameId);
      }
      window.clearTimeout(retryTimeoutId);
    };
  }, [
    messages,
    open,
    pathname,
    isLoadingState,
    selectedThreadId,
    isCurrentConversationWaiting,
  ]);

  useEffect(() => {
    if (!open || !canQuery || isCreditLocked) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [canQuery, isCreditLocked, open]);

  useEffect(() => {
    if (!open || isMobile) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isMobile, open]);

  const handleOpenAutoFocus = useCallback((event) => {
    event.preventDefault();
    composerInputRef.current?.focus();
  }, []);

  const handleDrawerOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        if (mobileSessionsOpenRef.current) {
          closeMobileHistoryPage();
        }

        setOpen(false);
        return;
      }

      setOpen(true);
    },
    [closeMobileHistoryPage]
  );

  const handleMessageRevealComplete = useCallback((messageId) => {
    if (!messageId) return;

    setRevealingMessageIds((currentRevealingMessageIds) => {
      if (!currentRevealingMessageIds.has(messageId)) {
        return currentRevealingMessageIds;
      }

      const nextRevealingMessageIds = new Set(currentRevealingMessageIds);
      nextRevealingMessageIds.delete(messageId);
      return nextRevealingMessageIds;
    });
  }, []);

  const appendLocalAssistantMessage = useCallback((content) => {
    scrollIntentRef.current = { type: "bottom", behavior: "smooth" };
    setMessages((previousMessages) => {
      const nextMessages = [
        ...previousMessages,
        {
          id: `local-assistant-${Date.now()}`,
          role: "assistant",
          content,
        },
      ];
      messagesRef.current = nextMessages;
      return nextMessages;
    });
  }, []);

  const appendLocalUserMessage = useCallback(
    (content, displayMetadata = {}) => {
      scrollIntentRef.current = { type: "bottom", behavior: "smooth" };
      setMessages((previousMessages) => {
        const nextMessages = [
          ...previousMessages,
          {
            id: `local-user-${Date.now()}`,
            role: "user",
            content,
            displayKind: displayMetadata.displayKind || null,
            displayPayload: displayMetadata.displayPayload || null,
          },
        ];
        messagesRef.current = nextMessages;
        return nextMessages;
      });
    },
    []
  );

  const handleSend = useCallback(
    async (overrideContent, options = {}) => {
      const content = (overrideContent || input).trim();
      const displayMessage = String(options.displayMessage || "").trim();
      const displayKind = options.displayKind || null;
      const displayPayload = options.displayPayload || null;
      const threadTitle = String(options.threadTitle || "").trim();
      const resolvedThreadId = Object.prototype.hasOwnProperty.call(
        options,
        "threadId"
      )
        ? options.threadId
        : selectedThreadId;

      if (
        !content ||
        isStartingRun ||
        isRunning ||
        isCreditLocked ||
        isLoadingState
      ) {
        return;
      }

      if (!store?.id) {
        appendLocalAssistantMessage("Select a store first to start chatting.");
        return;
      }

      appendLocalUserMessage(displayMessage || content, {
        displayKind,
        displayPayload,
      });
      setInput("");
      setIsStartingRun(true);
      setRunProgressMessage(QUEUED_PROGRESS_MESSAGE);

      try {
        const { data } = await llmChatStart({
          integrationId: store.id,
          message: content,
          threadId: resolvedThreadId,
          displayMessage: displayMessage || null,
          displayKind,
          displayPayload,
          threadTitle: threadTitle || null,
        });
        const runId = data?.llmChatStart?.runId;
        const threadId = data?.llmChatStart?.threadId;

        if (!runId || !threadId) {
          throw new Error("Chat run was not created");
        }

        scrollIntentRef.current = { type: "bottom", behavior: "smooth" };
        pollTokenRef.current += 1;
        const nextRunTarget = {
          runId,
          integrationId: store.id,
          threadId,
          threadTitle: resolvedThreadId
            ? sessions.find((session) => session.id === resolvedThreadId)
                ?.title ||
              displayMessage ||
              content
            : threadTitle || displayMessage || content,
          storeName: getStoreDisplayName(store),
          storeDomain: store?.domain || null,
          progressMessage: QUEUED_PROGRESS_MESSAGE,
        };
        currentRunTargetRef.current = nextRunTarget;
        setCurrentRunId(runId);
        setCurrentRunTarget(nextRunTarget);
        setRunStartedAt(Date.now());
        rememberSelectedThread(store.id, threadId);
        await loadSessions();
        await loadChatState({ threadId });
      } catch {
        appendLocalAssistantMessage(FAILED_RUN_MESSAGE);
      } finally {
        setIsStartingRun(false);
      }
    },
    [
      appendLocalAssistantMessage,
      appendLocalUserMessage,
      input,
      isCreditLocked,
      isLoadingState,
      isRunning,
      isStartingRun,
      llmChatStart,
      loadChatState,
      loadSessions,
      rememberSelectedThread,
      selectedThreadId,
      sessions,
      store,
    ]
  );

  useEffect(() => {
    const launchRequest = pendingAssistantLaunchRef.current;

    if (
      !launchRequest?.autoSend ||
      !open ||
      launchRequest.integrationId !== store?.id
    ) {
      return;
    }

    if (isStartingRun || isRunning || isCreditLocked) {
      pendingAssistantLaunchRef.current = null;
      return;
    }

    if (!chatState || !creditStatus || isLoadingState) {
      return;
    }

    pendingAssistantLaunchRef.current = null;
    void handleSend(launchRequest.prompt, {
      threadId: null,
      displayMessage: launchRequest.displayMessage,
      displayKind: launchRequest.displayKind,
      displayPayload: launchRequest.displayPayload,
      threadTitle: launchRequest.threadTitle,
    });
  }, [
    chatState,
    creditStatus,
    handleSend,
    isCreditLocked,
    isLoadingState,
    isRunning,
    isStartingRun,
    open,
    store?.id,
  ]);

  const onComposerKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const onComposerChange = useCallback((event) => {
    const nextInput = event.target.value;
    setInput(nextInput);

    if (!nextInput.trim()) {
      event.target.style.height = "";
      setIsComposerMultiline(false);
    }
  }, []);

  const handleSelectPrompt = useCallback(
    (prompt) => {
      setInput(prompt);
      focusComposerInput();
    },
    [focusComposerInput]
  );

  const weeklyTokenLimit = Math.max(
    0,
    Number(creditStatus?.weeklyTokenLimit || 0)
  );
  const millisUntilReset = creditStatus?.weekEndsAt
    ? new Date(creditStatus.weekEndsAt).getTime() - Date.now()
    : null;
  const daysUntilReset =
    millisUntilReset === null
      ? null
      : Math.max(0, Math.ceil(millisUntilReset / (24 * 60 * 60 * 1000)));
  const resetDays = daysUntilReset ?? 0;
  const resetDaysLabel = `${resetDays} ${resetDays === 1 ? "day" : "days"}`;
  const isSendBlocked =
    isStartingRun || isRunning || hasAnotherActiveRun || isLoadingState;
  const arePromptSuggestionsDisabled = isSendBlocked;
  const isSendDisabled = isSendBlocked || !input.trim();
  const currentChatTitle = selectedThreadId
    ? sessions.find((session) => session.id === selectedThreadId)?.title ||
      "Current chat"
    : input.trim()
      ? "Drafting new chat"
      : "New chat";
  const currentStoreReadyThreadIds = useMemo(() => {
    const integrationId = store?.id;
    if (!integrationId) return new Set();

    return new Set(
      Object.values(readyTargetsByKey)
        .filter(
          (target) =>
            target.integrationId === integrationId &&
            target.threadId !== selectedThreadId
        )
        .map((target) => target.threadId)
    );
  }, [readyTargetsByKey, selectedThreadId, store?.id]);
  const readyTarget = useMemo(() => {
    const targets = Object.values(readyTargetsByKey).filter(
      (target) => getTargetKey(target) !== currentThreadKey
    );

    return (
      targets.find((target) => target.integrationId === store?.id) ||
      targets[0] ||
      null
    );
  }, [currentThreadKey, readyTargetsByKey, store?.id]);
  const showEmptyState =
    !isLoadingState &&
    messages.length === 0 &&
    !isSelectedThreadRunning &&
    !selectedThreadId;
  const showClosedReadyState = !open && Boolean(readyTarget);
  const showClosedProcessingState = !open && !showClosedReadyState && isRunning;
  const closedProgressMessage =
    runProgressMessage ||
    activeRun?.progressMessage ||
    DEFAULT_PROGRESS_MESSAGE;
  const sessionListLoadingThreadId =
    isStartingRun && !selectedThreadId ? NEW_CHAT_LOADING_ID : loadingThreadId;
  const sessionListActiveThreadId =
    currentStoreActiveThreadId ||
    (isCurrentConversationWaiting && selectedThreadId
      ? selectedThreadId
      : null);
  const isAssistantUnavailable = !canQuery;

  const triggerLabel = showClosedProcessingState
    ? compactLabel(closedProgressMessage)
    : showClosedReadyState
      ? "Reply ready"
      : "AI Consultant";
  const triggerIcon = showClosedProcessingState ? (
    <Loader2 className="size-4 animate-spin" />
  ) : showClosedReadyState ? (
    <span className="relative flex size-4 items-center justify-center">
      <Sparkles className="size-4" />
      <Sparkles className="absolute inset-0 size-4 animate-ping opacity-70" />
    </span>
  ) : (
    <MessageSquare className="size-4" />
  );
  const triggerButton = (
    <Button
      type="button"
      variant={
        isAssistantUnavailable || isCreditLocked ? "secondary" : "default"
      }
      aria-disabled={isAssistantUnavailable}
      tabIndex={isAssistantUnavailable ? -1 : undefined}
      onClick={(event) => {
        if (isAssistantUnavailable) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        readyTarget ? openAssistantTarget(readyTarget) : setOpen(true);
      }}
      className={cn(
        "pointer-events-auto gap-2 shadow-lg",
        isAssistantUnavailable &&
          "cursor-not-allowed opacity-80 shadow-none hover:bg-secondary",
        showClosedReadyState && "ring-primary/40 ring-1"
      )}
    >
      {triggerIcon}
      {triggerLabel}
    </Button>
  );
  const unavailableTriggerButton = (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <span
          className="pointer-events-auto inline-flex rounded-md outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          aria-label="AI Consultant is unavailable until a store is connected"
        >
          {triggerButton}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        sideOffset={10}
        className="!max-w-80 !rounded-xl border !px-4 !py-3 text-left shadow-xl"
      >
        <div className="space-y-1.5">
          <p className="text-sm leading-none font-semibold">
            AI Consultant unlocks after a store is available
          </p>
          <p className="text-muted-foreground text-xs leading-5">
            Connect Shopify or try the demo store to ask questions about profit,
            products, orders, campaigns, and customer behavior.
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );

  const renderSessionList = (variant = "sidebar") => (
    <AssistantSessionList
      variant={variant}
      sessions={sessions}
      selectedThreadId={selectedThreadId}
      isLoading={isLoadingSessions}
      loadingThreadId={sessionListLoadingThreadId}
      activeThreadId={sessionListActiveThreadId}
      archivingThreadIds={archivingThreadIds}
      readyThreadIds={currentStoreReadyThreadIds}
      newChatLoadingId={NEW_CHAT_LOADING_ID}
      isNewChatDisabled={false}
      isArchiveDisabled={false}
      onNewChat={startNewChat}
      onSelectSession={selectSession}
      onArchiveSession={archiveSession}
    />
  );

  const mobileHistoryPage = (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <div className="border-border/70 border-b px-4 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-ml-1 size-11 shrink-0 rounded-full bg-muted/45 hover:bg-muted/70"
            onClick={closeMobileHistoryPage}
            aria-label="Back to chat"
          >
            <ChevronLeft className="size-5" />
          </Button>

          <div className="min-w-0">
            <p className="text-lg leading-tight font-semibold">Chat history</p>
            <p className="text-muted-foreground truncate text-sm">
              Switch analyses or start fresh.
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">{renderSessionList("mobile")}</div>
    </div>
  );

  const assistantPanel = (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <AssistantHeader
        storeDescription={store?.description || "No store selected"}
        currentChatTitle={currentChatTitle}
        isMobile={isMobile}
        creditStatus={creditStatus}
        sourcesOfTruth={sourcesOfTruth}
        onOpenHistory={openMobileHistoryPage}
        onClose={() => setOpen(false)}
      />

      <div className="flex min-h-0 flex-1">
        {!isMobile ? (
          <aside className="border-border/70 hidden w-56 shrink-0 border-r lg:block">
            {renderSessionList()}
          </aside>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <AssistantConversation
            scrollAreaRef={scrollAreaRef}
            isLoadingState={isLoadingState}
            messages={messages}
            showEmptyState={showEmptyState}
            suggestions={suggestions}
            isComposerDisabled={arePromptSuggestionsDisabled}
            isSelectedThreadRunning={isCurrentConversationWaiting}
            runProgressMessage={runProgressMessage}
            revealingMessageIds={revealingMessageIds}
            onSelectPrompt={handleSelectPrompt}
            onMessageRevealComplete={handleMessageRevealComplete}
          />

          <AssistantComposer
            isMobile={isMobile}
            isCreditLocked={isCreditLocked}
            weeklyTokenLimit={weeklyTokenLimit}
            resetDaysLabel={resetDaysLabel}
            hasAnotherActiveRun={hasAnotherActiveRun}
            currentStoreId={store?.id}
            activeRunTarget={activeRunTarget}
            activeRunProgressMessage={
              runProgressMessage || activeRun?.progressMessage
            }
            readyTarget={readyTarget}
            input={input}
            isComposerMultiline={isComposerMultiline}
            isComposerDisabled={false}
            isSendDisabled={isSendDisabled}
            isStartingRun={isStartingRun}
            composerInputRef={composerInputRef}
            onContact={openContactDialog}
            onOpenTarget={openAssistantTarget}
            onComposerChange={onComposerChange}
            onComposerKeyDown={onComposerKeyDown}
            onSend={() => handleSend()}
          />
        </div>
      </div>
    </div>
  );

  const mobileDrawerContent = mobileSessionsOpen
    ? mobileHistoryPage
    : assistantPanel;

  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-40">
      {isAssistantUnavailable ? (
        unavailableTriggerButton
      ) : isMobile ? (
        <Drawer open={open} onOpenChange={handleDrawerOpenChange}>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
          <DrawerContent
            className={cn(
              "bg-background pointer-events-auto z-50 overflow-hidden border p-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0",
              "h-[92dvh] rounded-t-xl pb-[env(safe-area-inset-bottom)] data-[vaul-drawer-direction=bottom]:max-h-[92dvh]"
            )}
            onOpenAutoFocus={handleOpenAutoFocus}
          >
            {mobileDrawerContent}
          </DrawerContent>
        </Drawer>
      ) : (
        <>
          {triggerButton}
          {open ? (
            <>
              <div
                aria-hidden="true"
                className="pointer-events-auto fixed inset-0 z-40"
                onClick={() => setOpen(false)}
              />
              <div
                role="dialog"
                aria-modal="false"
                className={cn(
                  "bg-background pointer-events-auto fixed right-4 bottom-20 z-50 overflow-hidden border shadow-2xl focus:outline-none focus-visible:outline-none focus-visible:ring-0",
                  "h-[min(85dvh,52rem)] w-[min(64rem,calc(100vw-2rem))] rounded-2xl"
                )}
              >
                {assistantPanel}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export const StoreAssistantModal = () => {
  return <StoreAssistantModalContent />;
};
